import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { BookingCompanyLine } from 'features/Bookings/components/CompanyLine'
import { BookingEmails } from 'features/Bookings/components/Emails'
import { BookingPayment } from 'features/Bookings/components/Payment'
import { BookingService } from 'features/Bookings/components/Service'
import { BookingStatus, getStatusColor } from 'features/Bookings/components/Status'
import { SupportInteractions } from 'features/Bookings/components/SupportInteractions'
import { BookingTickets } from 'features/Bookings/components/Tickets'
import { BookingTicketsServiceFee } from 'features/Bookings/components/TicketsServiceFee'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { isSameTrip } from 'resolvers/bookings'
import {
  canApplyInsurance,
  canCancelInsurance,
  canCancelTickets,
  canConfirmBookingPayment,
  canEditBooking,
  canMoveBookingTrip,
  canRegisterSupportInteraction,
  canSendEmailBooking,
  canWrite
} from 'features/Users/utils'
import BookingAncillaries from 'features/Bookings/components/BookingAncillaries'
import { GTBookingV2Status } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { BookingActions } from './Actions'
import { safeFormatMessage } from 'utils/intl'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import { BookingSalesChannel } from 'features/Bookings/components/BookingSalesChannel'
import { BuyerUserAccountDropdown } from 'features/Bookings/components/BuyerDropdown'
import { isEmpty } from 'lodash'
import { IBookingCollection } from 'features/Bookings/collections'

const BuyerCell = styled(Td)`
  > div {
    max-width: 200px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  > div:nth-child(1),
  > div:nth-child(2) {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const SecondaryInfo = styled.div`
  font-size: 13px;
  color: #9999bd;
  white-space: nowrap;
`

const CancellationSource = styled(SecondaryInfo)`
  color: ${getStatusColor(GTBookingV2Status.Cancelled)};
`

const VerticalInfo = styled.div`
  color: #5d59d7;
`

const BookingIdColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

interface IBookingsListProps {
  bookingsCollection: IBookingCollection
}

export const BookingsList = ({ bookingsCollection }: IBookingsListProps): ReactElement => {
  const bookings = bookingsCollection.items
  const intl = useIntl()
  const login = useLogin()

  const { policies } = login.accessToken

  const shouldShowActions =
    canWrite(FEATURES.BOOKING, policies) ||
    canEditBooking(policies) ||
    canMoveBookingTrip(policies) ||
    canSendEmailBooking(policies) ||
    canApplyInsurance(policies) ||
    canCancelInsurance(policies) ||
    canRegisterSupportInteraction(policies) ||
    canCancelTickets(policies)

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {shouldShowActions && <Th />}
            <Th>
              <FormattedMessage id="dictionary.id" defaultMessage="Id" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.date" defaultMessage="Date" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.buyer" defaultMessage="Buyer" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.service" defaultMessage="Service" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.amount" defaultMessage="Amount" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.tickets" defaultMessage="Tickets" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.ancillaries" defaultMessage="Ancillaries" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.serviceFee" defaultMessage="Service Fee" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.line" defaultMessage="Line" />
            </Th>
            <Th>
              <FormattedMessage id="bookings.list.businessClient" defaultMessage="Client" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.emails" defaultMessage="Emails" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.supportInteractions" defaultMessage="Support interactions" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {bookings.map((booking): ReactElement => {
            const {
              buyer,
              tickets,
              departureTrips,
              returnTrips,
              hasReturnTrips,
              hasReturnTickets,
              hasDepartureTrips,
              hasDepartureTickets,
              hasReservation,
              isBookingCancelled,
              isCancelledByUser,
              isPartiallyCancelledByUser
            } = booking

            const showDepartureTrips =
              (hasDepartureTrips && !hasReservation) || (hasDepartureTrips && hasDepartureTickets)
            const showReturnTrips = isSameTrip(departureTrips, returnTrips)
              ? false
              : (hasReturnTrips && !hasReservation) || (hasReturnTrips && hasReturnTickets)

            return (
              <Tr key={booking.id}>
                {shouldShowActions && (
                  <Td top>
                    <BookingActions booking={booking} />
                  </Td>
                )}
                <Td top>
                  <BookingIdColumnWrapper>
                    <CopyToClipboardWrapper>
                      <div>{booking.id.substring(0, 8)}</div>
                      <CopyToClipboard str={booking.id} />
                    </CopyToClipboardWrapper>
                    <BookingSalesChannel booking={booking} />
                  </BookingIdColumnWrapper>
                </Td>
                <Td top>
                  <BookingStatus status={booking.status} />
                  {isBookingCancelled && isCancelledByUser && (
                    <CancellationSource>
                      <FormattedMessage id="bookings.cancelledByUser" defaultMessage="(User)" />
                    </CancellationSource>
                  )}
                  {isBookingCancelled && isPartiallyCancelledByUser && (
                    <CancellationSource>
                      <FormattedMessage id="bookings.cancelledByUser" defaultMessage="(Partially User)" />
                    </CancellationSource>
                  )}
                </Td>
                <Td top>
                  <FormattedDate
                    intl={intl}
                    value={booking.confirmedAt || booking.createdAt}
                    month="2-digit"
                    day="2-digit"
                    year="numeric"
                  />
                  <SecondaryInfo>
                    <FormattedTimeByDate intl={intl} date={booking.confirmedAt || booking.createdAt} />
                  </SecondaryInfo>
                </Td>
                <BuyerCell top>
                  {buyer ? (
                    <>
                      <div title={`${buyer.firstName} ${buyer.lastName}`}>
                        {buyer.firstName} {buyer.lastName}
                      </div>
                      <SecondaryInfo title={buyer.email}>{buyer.email}</SecondaryInfo>
                      <SecondaryInfo>{buyer.phone}</SecondaryInfo>
                      {!isEmpty(booking.userAccount) && <BuyerUserAccountDropdown userAccount={booking.userAccount} />}
                    </>
                  ) : (
                    '-'
                  )}
                </BuyerCell>
                <Td top>
                  <BookingService
                    departureTrips={departureTrips}
                    returnTrips={returnTrips}
                    showDepartureTrips={showDepartureTrips}
                    showReturnTrips={showReturnTrips}
                  />
                  <VerticalInfo>
                    {safeFormatMessage({ intl, messages: verticalMessages, key: booking.vertical })}
                    {booking.isHomePickup ? (
                      <>
                        {' '}
                        <FormattedMessage id="bookings.homePickup" defaultMessage="(Home pickup)" />
                      </>
                    ) : null}
                  </VerticalInfo>
                </Td>
                <Td top>
                  {hasReservation || booking.hasPaidStatus ? (
                    <BookingPayment booking={booking} canWrite={canConfirmBookingPayment(login.accessToken.policies)} />
                  ) : (
                    '-'
                  )}
                </Td>
                <Td top>
                  <BookingTickets
                    booking={booking}
                    tickets={tickets}
                    departureTrips={departureTrips}
                    returnTrips={returnTrips}
                    showDepartureTrips={showDepartureTrips}
                    showReturnTrips={showReturnTrips}
                  />
                </Td>
                <Td top>
                  <BookingAncillaries
                    booking={booking}
                    tickets={tickets}
                    departureTrips={departureTrips}
                    returnTrips={returnTrips}
                    showDepartureTrips={showDepartureTrips}
                    showReturnTrips={showReturnTrips}
                  />
                </Td>
                <Td top>
                  <BookingTicketsServiceFee
                    tickets={tickets}
                    departureTrips={departureTrips}
                    returnTrips={returnTrips}
                    showDepartureTrips={showDepartureTrips}
                    showReturnTrips={showReturnTrips}
                    currencyCode={booking.currency.code === 'USD' ? 'USD' : undefined}
                  />
                </Td>
                <Td top>
                  <BookingCompanyLine
                    departureTrips={departureTrips}
                    returnTrips={returnTrips}
                    showDepartureTrips={showDepartureTrips}
                    showReturnTrips={showReturnTrips}
                  />
                </Td>
                <Td top>{booking.businessClient?.name || '-'}</Td>
                <Td top>
                  <BookingEmails booking={booking} />
                </Td>
                <Td top>
                  <SupportInteractions supportInteractions={booking.supportInteractions} showUser />
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
